import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Spin } from "antd";
import { Context, Hooks, Util } from "@xt/xtbase";
import { toUtc } from "utils/date";
import { get_inviteRebateFlowV2, get_inviteSummaryV2 } from "api/referral";
import { pickRfapiPagedList } from "@/components/Pages/referral/utils/pickRfapiPagedList";
import styles from "./index.module.less";

const { XtContext } = Context;
const { useTranslation } = Hooks;
const { Big, getUrl } = Util;

const PAGE_SIZE = 20;
const SCROLL_LOAD_EDGE = 48;
/** Bonus 区金额展示小数位数（汇总 + 列表） */
const BONUS_DECIMALS = 4;
const ZERO_BONUS_AMOUNT = Big(0).toFixedMaxCy(BONUS_DECIMALS);

/** 与 GET /profile/invite/v2/rebate-flow 的 rebateType 查询参数一致 */
const REBATE_FLOW_TYPE_AI_COPY_TRADE = "AI_COPY_TRADE";

function mapRebateKind(rebateType) {
  if (rebateType === "INVITE") return "invite";
  if (rebateType === REBATE_FLOW_TYPE_AI_COPY_TRADE) return "aicopy";
  return "trade";
}

/** 将接口行映射为表格行；分页游标：ALL 用 rebateTime(ms)，单类型用库表主键 id */
function mapFlowItems(items) {
  return (items || []).map((item, index) => ({
    key: item?.id != null ? String(item.id) : `${item?.rebateTime ?? 0}-${index}`,
    rebateTime: item?.rebateTime != null ? Number(item.rebateTime) : null,
    rawId: item?.id != null ? Number(item.id) : null,
    kind: mapRebateKind(item?.rebateType),
    amount: item?.rebateAmountU,
    currency: "USDT",
  }));
}

function buildRebateFlowParams(rebateType, lastRow) {
  const p = {
    rebateType: rebateType || "ALL",
    direction: "NEXT",
    limit: PAGE_SIZE,
  };
  if (!lastRow) return p;

  if (rebateType === "ALL") {
    if (lastRow.rebateTime != null && !Number.isNaN(Number(lastRow.rebateTime))) {
      p.fromId = Number(lastRow.rebateTime);
    }
  } else if (lastRow.rawId != null && !Number.isNaN(Number(lastRow.rawId))) {
    p.fromId = Number(lastRow.rawId);
  }
  return p;
}

export default function Bonus() {
  const t = useTranslation();
  const [appState] = React.useContext(XtContext);
  const { loginStatus } = appState;

  const [loginQuery, setLoginQuery] = useState("");
  const [filterKey, setFilterKey] = useState("ALL");
  const [rows, setRows] = useState([]);
  const [summaryTotalUsdt, setSummaryTotalUsdt] = useState(ZERO_BONUS_AMOUNT);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  const loadingMoreLock = useRef(false);
  const tableBodyRef = useRef(null);
  const scrollSentinelRef = useRef(null);
  const loadMoreRef = useRef(() => {});

  useEffect(() => {
    setLoginQuery("?backurl=" + encodeURIComponent(typeof window !== "undefined" ? window.location.href : ""));
  }, []);

  const chips = useMemo(
    () => [
      { key: "ALL", label: t("home.referralBonusFilterAll") },
      { key: "TRADE", label: t("home.referralBonusFilterTrade") },
      { key: "INVITE", label: t("home.referralBonusFilterInvite") },
      { key: REBATE_FLOW_TYPE_AI_COPY_TRADE, label: t("home.referralBonusFilterAiCopy") },
    ],
    [t]
  );

  useEffect(() => {
    if (loginStatus != 1) {
      setRows([]);
      setSummaryTotalUsdt(ZERO_BONUS_AMOUNT);
      setLoading(false);
      setHasMore(false);
      return;
    }

    let disposed = false;
    const fetchData = async () => {
      setLoading(true);
      try {
        const [summaryRes, flowRes] = await Promise.all([
          get_inviteSummaryV2({
            params: {
              timeRange: "ALL",
            },
            isAutoPopError: false,
          }),
          get_inviteRebateFlowV2({
            params: buildRebateFlowParams(filterKey || "ALL", null),
            isAutoPopError: false,
          }),
        ]);

        if (disposed) return;

        const summary = summaryRes && typeof summaryRes === "object" ? summaryRes : {};
        const totalUsdt = Big(summary?.tradingCommissionAmount || 0)
          .plus(summary?.inviteRewardAmount || 0)
          .plus(summary?.aiCopyTradeCommissionAmount || 0)
          .toFixedMaxCy(BONUS_DECIMALS);
        const { items, hasNext } = pickRfapiPagedList(flowRes);

        setSummaryTotalUsdt(totalUsdt);
        setRows(mapFlowItems(items));
        setHasMore(hasNext);
      } catch (error) {
        if (disposed) return;
        setRows([]);
        setSummaryTotalUsdt(ZERO_BONUS_AMOUNT);
        setHasMore(false);
      } finally {
        if (!disposed) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      disposed = true;
    };
  }, [Big, filterKey, loginStatus]);

  const loadMore = useCallback(async () => {
    if (loginStatus != 1 || loading || loadingMore || loadingMoreLock.current) return;
    if (!hasMore) return;
    const last = rows[rows.length - 1];
    if (!last) return;

    const rt = filterKey || "ALL";
    if (rt === "ALL" && (last.rebateTime == null || Number.isNaN(Number(last.rebateTime)))) return;
    if (rt !== "ALL" && (last.rawId == null || Number.isNaN(Number(last.rawId)))) return;

    loadingMoreLock.current = true;
    setLoadingMore(true);
    try {
      const res = await get_inviteRebateFlowV2({
        params: buildRebateFlowParams(rt, last),
        isAutoPopError: false,
      });
      const { items: list, hasNext: nextHas } = pickRfapiPagedList(res);
      setHasMore(nextHas);
      if (!list.length) return;
      const mapped = mapFlowItems(list);
      setRows((prev) => {
        const seen = new Set(prev.map((r) => r.key));
        const merged = [...prev];
        for (const row of mapped) {
          if (!seen.has(row.key)) {
            seen.add(row.key);
            merged.push(row);
          }
        }
        return merged;
      });
    } catch {
      // 静默失败，避免打断浏览
    } finally {
      loadingMoreLock.current = false;
      setLoadingMore(false);
    }
  }, [loginStatus, loading, loadingMore, hasMore, rows, filterKey]);

  loadMoreRef.current = loadMore;

  useEffect(() => {
    const root = tableBodyRef.current;
    const sentinel = scrollSentinelRef.current;
    if (!root || !sentinel) return;
    if (!hasMore || loading) return;

    const io = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        if (loadingMoreLock.current) return;
        loadMoreRef.current();
      },
      { root, rootMargin: `${SCROLL_LOAD_EDGE}px`, threshold: 0 }
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [hasMore, loading, loadingMore, rows.length]);

  const fmtBonus = (amount, currency) => {
    const n = Big(amount ?? 0).toFixedMaxCy(BONUS_DECIMALS);
    const u = (currency || "USDT").toUpperCase();
    return u === "USDT" ? `${n} U` : `${n} ${u}`;
  };

  const fmtTime = (ts) => (ts != null && ts !== "" ? toUtc(Number(ts)) : "—");

  if (loginStatus != 1) {
    return (
      <div className={styles.wrap}>
        <div className={styles.loginHint}>
          <a href={getUrl("/accounts/login" + loginQuery)} className={styles.loginLink}>
            {t("header.login")}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.chips}>
        {chips.map((c) => (
          <button
            key={c.key}
            type="button"
            className={filterKey === c.key ? styles.chipActive : styles.chip}
            onClick={() => setFilterKey(c.key)}
          >
            {c.label}
          </button>
        ))}
      </div>

      <div className={styles.summaryCard}>
        <div className={styles.summaryInner}>
          <div className={styles.summaryLabel}>{t("home.referralBonusMyRebate")}</div>
          <div className={styles.summaryValue}>{summaryTotalUsdt} USDT</div>
        </div>
      </div>

      <div className={styles.tableCard}>
        <div className={styles.tableHead}>
          <div className={styles.thTime}>{t("home.referralBonusColTime")}</div>
          <div className={styles.th}>{t("home.referralBonusColType")}</div>
          <div className={styles.thEnd}>{t("home.referralBonusColBonus")}</div>
        </div>
        <div className={styles.tableBody} ref={tableBodyRef}>
          {loading ? (
            <div className={styles.loadingBox}>
              <Spin size="small" />
            </div>
          ) : rows.length === 0 ? (
            <div className={styles.empty}>{t("home.referralBonusEmpty")}</div>
          ) : (
            <>
              {rows.map((row, i) => (
                <div key={row.key} className={i % 2 === 1 ? styles.tr : styles.trAlt}>
                  <div className={styles.tdTime}>{fmtTime(row.rebateTime)}</div>
                  <div className={styles.td}>
                    <span
                      className={
                        row.kind === "trade"
                          ? styles.typePillTrade
                          : row.kind === "aicopy"
                            ? styles.typePillAiCopy
                            : styles.typePillInvite
                      }
                    >
                      {row.kind === "trade"
                        ? t("home.referralBonusFilterTrade")
                        : row.kind === "aicopy"
                          ? t("home.referralBonusFilterAiCopy")
                          : t("home.referralBonusFilterInvite")}
                    </span>
                  </div>
                  <div
                    className={
                      row.kind === "trade"
                        ? `${styles.tdBonus} ${styles.tdBonusTrade}`
                        : row.kind === "aicopy"
                          ? `${styles.tdBonus} ${styles.tdBonusAiCopy}`
                          : `${styles.tdBonus} ${styles.tdBonusInvite}`
                    }
                  >
                    {fmtBonus(row.amount, row.currency)}
                  </div>
                </div>
              ))}
              <div ref={scrollSentinelRef} className={styles.scrollSentinel} aria-hidden />
              {loadingMore && (
                <div className={styles.loadMoreRow}>
                  <Spin size="small" />
                </div>
              )}
              {!loadingMore && hasMore && rows.length > 0 && (
                <div className={styles.loadMoreHint}>{t("home.referralMembersScrollMore")}</div>
              )}
            </>
          )}
        </div>
      </div>

      <div className={styles.footer}>{t("home.referralBonusFooter").replace("{count}", String(rows.length))}</div>
    </div>
  );
}
